/**
 * LMS — Carrousel de la section « Produits complémentaires ».
 *
 * Wrapper Swiper pour le carrousel de produits d'une collection
 * (sections/lms-produits-complementaires.liquid), sur le même principe que
 * <featured-collection> (assets/featured-collection.js) : Swiper est utilisé
 * en desktop/tablette (>= FoxTheme.config.mediaQueryMobile), et un simple
 * scroll horizontal CSS (.swipe-mobile) prend le relais en mobile.
 *
 * Contrairement à <featured-collection>, les flèches sont toujours affichées
 * en ligne à côté du titre (jamais en overlay flottant) : aucun calcul de
 * position n'est nécessaire ici.
 */
if (!customElements.get('lms-complementary-products')) {
	/**
	 * @class
	 * @extends HTMLElement
	 */
	class LmsComplementaryProducts extends HTMLElement {
		// Selectors
		#selectors = {
			itemsWrapper: '.lms-cp__items',
			nextButton: '.swiper-button-next',
			prevButton: '.swiper-button-prev',
			pagination: '.swiper-pagination',
		};

		// Cached elements
		#elements = {};

		/** @type {Element|null} Racine `.section-{id}` englobante */
		#section = null;

		/** @type {boolean} */
		#enableSlider = false;

		/** @type {number} */
		#items = 1;

		/** @type {number} */
		#tabletItems = 1;

		/** @type {object|false} Instance FoxTheme.Carousel active */
		#sliderInstance = false;

		/** @type {MediaQueryList|null} */
		#mql = null;

		/**
		 * Lifecycle - Component connected to DOM
		 * @private
		 */
		connectedCallback() {
			this.#section = this.closest(`.section-${this.dataset.sectionId}`);
			this.#enableSlider = this.dataset.enableSlider === 'true';
			this.#items = parseInt(this.dataset.items, 10) || 1;
			this.#tabletItems = parseInt(this.dataset.tabletItems, 10) || 1;

			this.#cacheElements();

			if (!this.#enableSlider || !this.#elements.itemsWrapper) return;

			this.#mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
			this.#mql.addEventListener('change', this.#handleMediaChange);
			this.#init();
		}

		/**
		 * Lifecycle - Component removed from DOM
		 * @private
		 */
		disconnectedCallback() {
			this.#mql?.removeEventListener('change', this.#handleMediaChange);
			this.#destroySlider();
		}

		/**
		 * Cache DOM elements
		 * @private
		 */
		#cacheElements() {
			this.#elements = {
				itemsWrapper: this.querySelector(this.#selectors.itemsWrapper),
				nextButton: this.#section?.querySelector(this.#selectors.nextButton),
				prevButton: this.#section?.querySelector(this.#selectors.prevButton),
				pagination: this.#section?.querySelector(this.#selectors.pagination),
			};
		}

		/**
		 * Handle mobile/desktop breakpoint change
		 * @private
		 */
		#handleMediaChange = () => {
			this.#init();
		};

		/**
		 * Bascule slider Swiper (desktop/tablette) / scroll CSS (mobile)
		 * @private
		 */
		#init() {
			if (FoxTheme.config.mqlMobile) {
				this.#destroySlider();
			} else {
				this.#initSlider();
			}
		}

		/**
		 * Initialise l'instance Swiper
		 * @private
		 */
		#initSlider() {
			if (typeof this.#sliderInstance === 'object') return;

			const { itemsWrapper, nextButton, prevButton, pagination } = this.#elements;
			const columnGap = FoxTheme.utils.getGridColumnGap(itemsWrapper);

			const sliderOptions = {
				slidesPerView: this.#tabletItems > 3 ? 3 : this.#tabletItems,
				spaceBetween: columnGap.tablet,
				navigation: {
					nextEl: nextButton,
					prevEl: prevButton,
				},
				pagination: pagination ? { el: pagination, clickable: true } : false,
				breakpoints: {
					1024: {
						slidesPerView: this.#items > 4 ? 4 : this.#items,
						spaceBetween: columnGap.tabletLarge,
					},
					1280: {
						slidesPerView: this.#items,
						spaceBetween: columnGap.desktop,
					},
				},
				loop: true,
				threshold: 2,
			};

			this.classList.add('swiper');
			itemsWrapper.classList.remove('f-grid');
			itemsWrapper.classList.add('swiper-wrapper');

			this.#sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
			this.#sliderInstance.init();

			this.#fixQuickviewDuplicate();
		}

		/**
		 * Détruit l'instance Swiper et revient à la grille CSS
		 * @private
		 */
		#destroySlider() {
			const { itemsWrapper } = this.#elements;

			this.classList.remove('swiper');
			itemsWrapper?.classList.remove('swiper-wrapper');
			itemsWrapper?.classList.add('f-grid');

			if (typeof this.#sliderInstance !== 'object') return;
			this.#sliderInstance.slider.destroy();
			this.#sliderInstance = false;
		}

		/**
		 * Le mode loop de Swiper clone des slides : dédoublonne les quick-view
		 * modals (même id) pour éviter deux modals identiques dans le DOM.
		 * @private
		 */
		#fixQuickviewDuplicate() {
			const seenIds = [];
			this.querySelectorAll('quick-view-modal').forEach((modal) => {
				const modalId = modal.getAttribute('id');
				if (seenIds.includes(modalId)) {
					modal.remove();
				} else {
					seenIds.push(modalId);
				}
			});
		}
	}

	customElements.define('lms-complementary-products', LmsComplementaryProducts);
}
